import { useEffect, useRef, useState } from 'react';

/** 長押しの完了だけを通知する。クリックやキーの自動リピートでは実行しない。 */
export function HoldToConfirm({
  disabled = false,
  onConfirm,
  children,
}: {
  disabled?: boolean;
  onConfirm: () => void;
  children: string;
}) {
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('1.5 秒押し続けて確認');
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const confirmed = useRef(false);
  const callback = useRef(onConfirm);
  callback.current = onConfirm;
  const cancel = () => {
    // 押している途中で離したときだけ「取り消し」と伝える（フォーカスが外れただけでは出さない）
    const holding = timer.current !== undefined;
    clearInterval(timer.current);
    timer.current = undefined;
    setProgress(0);
    if (holding && !confirmed.current) setMessage('長押しを取り消しました');
    confirmed.current = false;
  };
  useEffect(() => {
    if (disabled) cancel();
    return () => clearInterval(timer.current);
  }, [disabled]);
  const start = () => {
    if (disabled || timer.current || confirmed.current) return;
    const started = Date.now();
    setMessage('押し続けてください');
    timer.current = setInterval(() => {
      const next = Math.min(1, (Date.now() - started) / 1500);
      setProgress(next);
      if (next < 1) return;
      clearInterval(timer.current);
      timer.current = undefined;
      confirmed.current = true;
      setMessage('確認しました');
      callback.current();
    }, 50);
  };
  return (
    <>
      <button
        type="button"
        className="btn danger hold-confirm"
        disabled={disabled}
        onPointerDown={(event) => {
          if (event.button !== 0 || event.isPrimary === false) return;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          start();
        }}
        onPointerUp={cancel}
        onPointerCancel={cancel}
        onLostPointerCapture={cancel}
        onBlur={cancel}
        onKeyDown={(event) => {
          if (![' ', 'Enter'].includes(event.key)) return;
          event.preventDefault();
          if (!event.repeat) start();
        }}
        onKeyUp={(event) => {
          if (![' ', 'Enter'].includes(event.key)) return;
          event.preventDefault();
          cancel();
        }}
      >
        <span className="hold-fill" style={{ width: `${progress * 100}%` }} aria-hidden="true" />
        <span>{children}（長押し）</span>
      </button>
      <small role="status" aria-live="polite">
        {message}
      </small>
    </>
  );
}
